/**
 * UI layout preference — desktop vs Windows Phone–styl mobilní shell.
 *
 * auto    — podle šířky okna (≤768px = phone)
 * phone   — vynutit Metro shell
 * desktop — klasický sidebar (i na telefonu)
 *
 * Cache: localStorage `acm_ui_layout` (okamžitě, bez prefs roundtrip).
 */
import { useCallback, useEffect, useState } from 'react';

export type UiLayoutPref = 'auto' | 'phone' | 'desktop';
export type UiLayoutEffective = 'phone' | 'desktop';

const LS_KEY = 'acm_ui_layout';
/** Tailwind md — stejná hranice jako useViewport. */
export const UI_LAYOUT_MQ = '(max-width: 767.98px)';

export function readUiLayoutPref(): UiLayoutPref {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'phone' || v === 'desktop' || v === 'auto') return v;
  } catch { /* */ }
  return 'auto';
}

export function writeUiLayoutPref(pref: UiLayoutPref): void {
  try {
    localStorage.setItem(LS_KEY, pref);
  } catch { /* */ }
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.layout = resolveEffective(pref);
  }
  try {
    window.dispatchEvent(new CustomEvent('acm-ui-layout', { detail: pref }));
  } catch { /* */ }
}

export function resolveEffective(
  pref: UiLayoutPref = readUiLayoutPref(),
  narrow?: boolean,
): UiLayoutEffective {
  if (pref === 'phone') return 'phone';
  if (pref === 'desktop') return 'desktop';
  const isNarrow = narrow ??
    (typeof window !== 'undefined' ? window.matchMedia(UI_LAYOUT_MQ).matches : false);
  return isNarrow ? 'phone' : 'desktop';
}

/** Init před paint (main.tsx) — data-layout na <html>. */
export function initUiLayout(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.layout = resolveEffective();
}

function mqSubscribe(mq: MediaQueryList, fn: () => void): () => void {
  // Safari < 14: addListener / removeListener (ne addEventListener)
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }
  const legacy = mq as MediaQueryList & {
    addListener?: (cb: () => void) => void;
    removeListener?: (cb: () => void) => void;
  };
  legacy.addListener?.(fn);
  return () => legacy.removeListener?.(fn);
}

export function useUiLayout(): {
  pref: UiLayoutPref;
  effective: UiLayoutEffective;
  setPref: (p: UiLayoutPref) => void;
  isPhone: boolean;
  /** Viewport je úzký (telefon/detekce) — FAB má zůstat. */
  isNarrow: boolean;
  togglePhoneDesktop: () => void;
} {
  const [pref, setPrefState] = useState<UiLayoutPref>(() =>
    typeof window !== 'undefined' ? readUiLayoutPref() : 'auto',
  );
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(UI_LAYOUT_MQ).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(UI_LAYOUT_MQ);
    const onMq = () => setNarrow(mq.matches);
    const unsub = mqSubscribe(mq, onMq);
    const onPref = (e: Event) => {
      const d = (e as CustomEvent<UiLayoutPref>).detail;
      if (d === 'phone' || d === 'desktop' || d === 'auto') setPrefState(d);
      else setPrefState(readUiLayoutPref());
    };
    window.addEventListener('acm-ui-layout', onPref);
    return () => {
      unsub();
      window.removeEventListener('acm-ui-layout', onPref);
    };
  }, []);

  const setPref = useCallback((p: UiLayoutPref) => {
    writeUiLayoutPref(p);
    setPrefState(p);
  }, []);

  const effective = resolveEffective(pref, narrow);

  const togglePhoneDesktop = useCallback(() => {
    // Na mobilu přepínej výhradně phone ↔ desktop (ne auto — ať je předvídatelné)
    setPref(effective === 'phone' ? 'desktop' : 'phone');
  }, [effective, setPref]);

  useEffect(() => {
    document.documentElement.dataset.layout = effective;
  }, [effective]);

  return {
    pref,
    effective,
    setPref,
    isPhone: effective === 'phone',
    isNarrow: narrow,
    togglePhoneDesktop,
  };
}

/** Lightweight: jen isPhone (pro WidgetPanel / grid). */
export function useIsPhoneLayout(): boolean {
  return useUiLayout().isPhone;
}

const PHONE_NAV_KEY = 'acm_phone_nav_stack';

/** Historie cest pro „předchozí“ v app baru (max 12, bez /start). */
export function pushPhoneNav(path: string): void {
  try {
    if (!path || path === '/start' || path === '/phone' || path === '/') return;
    const raw = sessionStorage.getItem(PHONE_NAV_KEY);
    const stack: string[] = raw ? JSON.parse(raw) : [];
    if (stack[stack.length - 1] === path) return;
    const next = [...stack.filter((p) => p !== path), path].slice(-12);
    sessionStorage.setItem(PHONE_NAV_KEY, JSON.stringify(next));
  } catch { /* */ }
}

export function peekPhoneNavPrevious(current: string): string | null {
  try {
    const raw = sessionStorage.getItem(PHONE_NAV_KEY);
    const stack: string[] = raw ? JSON.parse(raw) : [];
    // poslední jiná než current
    for (let i = stack.length - 1; i >= 0; i--) {
      if (stack[i] && stack[i] !== current) return stack[i]!;
    }
  } catch { /* */ }
  return null;
}
